import { FC, useEffect, useState } from "react";
import ListItems from "./ListItems";
import ListHeader from "./ListHeader";
import ListFilter from "./ListFilter";
import {
  useAppSelector as useSelector,
  useAppDispatch as useDispatch,
} from "../../../store";
import { getCandidatesJobsOverCentresThunk } from "../../../store/candidate/thunks";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { PaginationLink } from "../../../store/superadmin/types";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../constants/app";
import { SortOrder } from "../../../store/vacancy/types";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import ViewCentre from "../../SuperAdmin/CentresAndUsers/Centres/ViewCentre";
import { getCentreDetailsThunk } from "../../../store/superadmin/thunks";
import { getIsStage3Activate } from "../Jobs/CandidateJobHeader";

const Centres: FC = () => {
  const dispatch = useDispatch();

  const documentsExpired = useSelector(
    (state) => state.onboarding.userpersonaldetails?.is_documents_expired
  );
  const allDetailsLists = useSelector(
    (state) => state.candidateJobs.candidatesJobsDetailsOverCentres
  );
  const jobpreferences = useSelector(
    (state) => state.onboarding.jobpreferences
  );
  const profileSteps = useSelector(
    (state) => state.onboarding.candidateProfileSteps
  );

  const isStage3Activate = getIsStage3Activate(profileSteps);

  const [showCentreViewModal, setShowCentreViewModal] =
    useState<boolean>(false);

  const [searchInput, setSearchInput] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [centresTotalPages, setCentresTotalPages] = useState<number>(1);
  const [centresPaginationLinks, setCentresPaginationLinks] = useState<
    PaginationLink[]
  >([]);
  const [perPage, setPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const [selectedSortBy, setSelectedSortBy] = useState<string>("business_name");
  const [selectedSortOrder, setSelectedSortOrder] = useState<SortOrder>("asc");
  const [selectCurrentListing, setSelectCurrentListing] =
    useState<boolean>(false);
  const [selectWorkedAtPreviously, setSelectWorkedAtPreviously] =
    useState<boolean>(false);
  const [selectDistanceAway, setSelectDistanceAway] = useState<number>(jobpreferences?.max_travel_distance ?? 10);
  const [selectedKm, setSelectedKm] = useState<number>(jobpreferences?.max_travel_distance ?? 10);

  /* Add delay to input the km */
  const debouncedKmSearch = useDebouncedValue(
    selectDistanceAway,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  useEffect(() => {
    setSelectedKm(debouncedKmSearch);
  }, [debouncedKmSearch]);
  /* end - delay to input the km */

  useEffect(() => {
    const getCandidatesJobsOverCentres = async () => {
      dispatch(showTransparentPreloaderAction());
      await dispatch(
        getCandidatesJobsOverCentresThunk({
          keyword: selectedKeyword,
          page: currentPage,
          per_page: perPage,
          sort_by: selectedSortBy,
          sort_order: selectedSortOrder,
          centre_with_current_listing: selectCurrentListing,
          centres_worked_previously: selectWorkedAtPreviously,
          distance_away: selectedKm,
        })
      );
      dispatch(hideTransparentPreloaderAction());
    };

    getCandidatesJobsOverCentres();
  }, [
    dispatch,
    selectedKeyword,
    currentPage,
    selectedSortBy,
    selectedSortOrder,
    perPage,
    selectCurrentListing,
    selectWorkedAtPreviously,
    selectedKm,
  ]);

  const handleCentreViewModal = async (id: number) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(getCentreDetailsThunk({ centre_id: id }));
    dispatch(hideTransparentPreloaderAction());
    setShowCentreViewModal(!false);
  };

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch); // Update the selected keyword state
      setCurrentPage(1); // Reset the current page to 1
    }
  }, [debouncedSearch]);

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (allDetailsLists && allDetailsLists?.total > 0) {
      setCentresTotalPages(allDetailsLists?.last_page || 1);
      setCentresPaginationLinks(allDetailsLists?.links);
    }
  }, [allDetailsLists]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  // Handle sorting logic
  const handleSortChange = (field: any) => {
    if (selectedSortBy === field) {
      setSelectedSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSelectedSortBy(field);
      setSelectedSortOrder("asc");
    }
  };

  return (
    <>
      <ViewCentre
        showViewModal={showCentreViewModal}
        setShowViewModal={setShowCentreViewModal}
      />

      <div className="d-flex flex-column gap-10">
        <ListFilter
          setSearchInput={setSearchInput}
          selectCurrentListing={selectCurrentListing}
          selectWorkedAtPreviously={selectWorkedAtPreviously}
          selectDistanceAway={selectDistanceAway}
          setSelectCurrentListing={setSelectCurrentListing}
          setSelectWorkedAtPreviously={setSelectWorkedAtPreviously}
          setSelectDistanceAway={setSelectDistanceAway}
        />

        <ListHeader
          selectedSortBy={selectedSortBy}
          selectedSortOrder={selectedSortOrder}
          handleSortChange={handleSortChange}
        />

        <ListItems
          allDetailsLists={allDetailsLists}
          handleCentreViewModal={handleCentreViewModal}
          currentPage={currentPage}
          totalPages={centresTotalPages}
          paginationLinks={centresPaginationLinks}
          onPageChange={handlePageChange}
          setPerPageList={setPerPage}
          setCurrentPage={setCurrentPage}
          isStage3Activate={isStage3Activate}
          documentsExpired={documentsExpired}
        />
      </div>
    </>
  );
};

export default Centres;
